import { QueryKey, queryOptions } from '@tanstack/react-query'
import {
    ActivitySharingLevelModel,
    ActivityStatusModel,
    AssetContractTypeModel,
    AssetTypeModel,
    BacklistSeasonYearModel,
    BookClubMaterialModel,
    BookClubModel,
    ContactAddressTypeModel,
    ImageTypeModel,
    LinkTypeModel,
    PrioLevelModel,
    ProductionTypeModel,
    PublishingHouseGroupModel,
    SearchConfigurationModel,
    SeasonYearModel,
    SharedInfoEbookModel,
    SharedInfoEditorialModel,
    SharedInfoPublishingModel,
    TimePlanEntryTypeModel,
    TimePlanPackageModel,
    UserAccessLevelModel,
} from 'ApiClient/metadata'

import { assetContractTypesGet, assetTypesGet } from 'Common/api/AssetMetadataApi'
import { contactTextTypesGet } from 'Common/api/ContactMetadataApi'
import { metadataServiceBundleGet } from 'Common/api/MetadataApi'
import {
    accountingMappingsGet,
    activityCategoriesGet,
    activityTemplatePackagesGet,
    agencyRightsCategoryGet,
    agencyRightsSubCategoryGet,
    awardsGet,
    backlistSeasonPeriodsGet,
    bindingCodesGet,
    bookBindingBindersGet,
    bookBindingBindingsGet,
    bookBindingSpineTypesGet,
    brandsGet,
    contactRolesGet,
    contractFormatsGet,
    contractTypesGet,
    costCentersGet,
    countriesGet,
    coverPlacementsGet,
    coverTypesGet,
    customerGroupsGet,
    customerSubGroupsGet,
    dispositionCodesGet,
    effortTypesGet,
    erpStatusCodesGet,
    foePapersGet,
    governingCodesGet,
    imageSheetPapersGet,
    imageSheetPrintingsGet,
    insideBulksGet,
    insidePapersGet,
    insidePrintingsGet,
    insideWeightsGet,
    interestAgesGet,
    internalCategoriesGet,
    jacketPapersGet,
    laminationsGet,
    languagesGet,
    layoutExpressionsGet,
    libraryCodePrefixesGet,
    libraryCodesGet,
    mainGroupsGet,
    postingGroupsGet,
    priceGroupPocketsGet,
    printedCoverPapersGet,
    printingsGet,
    productGroupsGet,
    productionStatusesGet,
    publishingHouseGroupsGet,
    publishingHousesGet,
    publishingListsGet,
    retailerChannelsGet,
    salesChannelsGet,
    salesStatusGet,
    seasonPeriodsGet,
    settingsBatteryGet,
    settingsCostTypesGet,
    settingsDiscountNumbersGet,
    settingsEducationGet,
    settingsErpFieldsGet,
    settingsSalesTypesGet,
    softCoverPapersGet,
    subgroupsGet,
    targetGroupThemesGet,
    targetGroupsGet,
    taxCodesGet,
    technicalProductionTypesGet,
    textTypesGet,
} from 'Common/api/WorkMetadataApi'
import { getQueryClient } from 'Common/base/queryClient'
import { MetadataItem } from 'Common/metadata'
import { RequestGcTime, RequestStaleTime } from 'Common/utils/helpers/HelperQuery'

const metadataServiceNames = [
    'activitySharingLevels',
    'activityStatus',
    'backlistSeasonYears',
    'bookClubMaterials',
    'bookClubs',
    'contactAddressTypes',
    'coverPapers',
    'imageTypes',
    'linkTypes',
    'prioLevels',
    'publishingHouseGroups',
    'productionLanguages',
    'productionTypes',
    'searchConfigurations',
    'seasonYears',
    'seasons',
    'sharedInfoEbook',
    'sharedInfoEditorial',
    'sharedInfoPublishing',
    'timePlanEntryTypes',
    'timePlanPackages',
    'userAccessLevels',
] as const

type MetadataServiceName = (typeof metadataServiceNames)[number]

const serviceBundleQuery = queryOptions({
    queryKey: ['metadata', '_serviceBundle'],
    queryFn: () => metadataServiceBundleGet(metadataServiceNames),
    staleTime: RequestStaleTime.metadata,
    gcTime: RequestGcTime.metadata,
})

const serviceGetter =
    <T>(name: MetadataServiceName) =>
    async (): Promise<T[]> => {
        const bundle = await getQueryClient().fetchQuery(serviceBundleQuery)
        return (bundle[name] ?? []) as T[]
    }

const metadataRegistry = {
    accountingMappings: accountingMappingsGet,
    activityCategories: activityCategoriesGet,
    activityTemplatePackages: activityTemplatePackagesGet,
    agencyRightsCategory: agencyRightsCategoryGet,
    agencyRightsSubCategory: agencyRightsSubCategoryGet,
    awards: awardsGet,
    backlistSeasonPeriods: backlistSeasonPeriodsGet,
    bindingCodes: bindingCodesGet,
    bookBindingBinders: bookBindingBindersGet,
    bookBindingBindings: bookBindingBindingsGet,
    bookBindingSpineTypes: bookBindingSpineTypesGet,
    brands: brandsGet,
    contactRoles: contactRolesGet,
    contractFormats: contractFormatsGet,
    contractTypes: contractTypesGet,
    costCenters: costCentersGet,
    countries: countriesGet,
    coverPlacements: coverPlacementsGet,
    coverTypes: coverTypesGet,
    customerGroups: customerGroupsGet,
    customerSubGroups: customerSubGroupsGet,
    dispositionCodes: dispositionCodesGet,
    effortTypes: effortTypesGet,
    erpStatusCodes: erpStatusCodesGet,
    foePapers: foePapersGet,
    governingCodes: governingCodesGet,
    imageSheetPapers: imageSheetPapersGet,
    imageSheetPrintings: imageSheetPrintingsGet,
    insideBulks: insideBulksGet,
    insidePapers: insidePapersGet,
    insidePrintings: insidePrintingsGet,
    insideWeights: insideWeightsGet,
    interestAges: interestAgesGet,
    internalCategories: internalCategoriesGet,
    jacketPapers: jacketPapersGet,
    laminations: laminationsGet,
    languages: languagesGet,
    layoutExpressions: layoutExpressionsGet,
    libraryCodePrefixes: libraryCodePrefixesGet,
    libraryCodes: libraryCodesGet,
    mainGroups: mainGroupsGet,
    postingGroups: postingGroupsGet,
    priceGroupPockets: priceGroupPocketsGet,
    printedCoverPapers: printedCoverPapersGet,
    printings: printingsGet,
    productGroups: productGroupsGet,
    publishingHouseGroups: publishingHouseGroupsGet,

    publishingHouseGroupsWithHouses:
        serviceGetter<PublishingHouseGroupModel>('publishingHouseGroups'),
    productionStatuses: productionStatusesGet,
    publishingHouses: publishingHousesGet,
    publishingLists: publishingListsGet,
    retailerChannels: retailerChannelsGet,
    salesChannels: salesChannelsGet,
    salesStatus: salesStatusGet,
    seasonPeriods: seasonPeriodsGet,
    softCoverPapers: softCoverPapersGet,
    subgroups: subgroupsGet,
    targetGroupThemes: targetGroupThemesGet,
    targetGroups: targetGroupsGet,
    taxCodes: taxCodesGet,
    technicalProductionTypes: technicalProductionTypesGet,
    textTypes: textTypesGet,

    battery: settingsBatteryGet,
    costTypes: settingsCostTypesGet,
    discountNumbers: settingsDiscountNumbersGet,
    education: settingsEducationGet,
    erpFields: settingsErpFieldsGet,
    salesTypes: settingsSalesTypesGet,

    assetContractTypes: assetContractTypesGet as () => Promise<AssetContractTypeModel[]>,
    assetTypes: assetTypesGet as () => Promise<AssetTypeModel[]>,

    contactTextTypes: contactTextTypesGet,

    activityStatus: serviceGetter<ActivityStatusModel>('activityStatus'),
    activitySharingLevels: serviceGetter<ActivitySharingLevelModel>('activitySharingLevels'),
    backlistSeasonYears: serviceGetter<BacklistSeasonYearModel>('backlistSeasonYears'),
    bookClubMaterials: serviceGetter<BookClubMaterialModel>('bookClubMaterials'),
    bookClubs: serviceGetter<BookClubModel>('bookClubs'),
    contactAddressTypes: serviceGetter<ContactAddressTypeModel>('contactAddressTypes'),
    coverPapers: serviceGetter<MetadataItem>('coverPapers'),
    imageTypes: serviceGetter<ImageTypeModel>('imageTypes'),
    linkTypes: serviceGetter<LinkTypeModel>('linkTypes'),
    prioLevels: serviceGetter<PrioLevelModel>('prioLevels'),
    productionLanguages: serviceGetter<MetadataItem>('productionLanguages'),
    productionTypes: serviceGetter<ProductionTypeModel>('productionTypes'),
    searchConfigurations: serviceGetter<SearchConfigurationModel>('searchConfigurations'),
    seasons: serviceGetter<MetadataItem>('seasons'),
    seasonYears: serviceGetter<SeasonYearModel>('seasonYears'),
    sharedInfoEbook: serviceGetter<SharedInfoEbookModel>('sharedInfoEbook'),
    sharedInfoEditorial: serviceGetter<SharedInfoEditorialModel>('sharedInfoEditorial'),
    sharedInfoPublishing: serviceGetter<SharedInfoPublishingModel>('sharedInfoPublishing'),
    timePlanEntryTypes: serviceGetter<TimePlanEntryTypeModel>('timePlanEntryTypes'),
    timePlanPackages: serviceGetter<TimePlanPackageModel>('timePlanPackages'),
    userAccessLevels: serviceGetter<UserAccessLevelModel>('userAccessLevels'),
}

export type MetadataName = keyof typeof metadataRegistry

export const workMetadataNames = [
    'sharedInfoPublishing',
    'sharedInfoEditorial',
    'sharedInfoEbook',
    'prioLevels',
    'contactRoles',
    'activityCategories',
    'activityStatus',
    'activityTemplatePackages',
    'targetGroups',
    'targetGroupThemes',
    'productionLanguages',
    'publishingHouses',
    'contractTypes',
    'publishingLists',
] as const satisfies readonly MetadataName[]

export const editionMetadataNames = [
    'dispositionCodes',
    'taxCodes',
    'salesStatus',
    'contractTypes',
    'libraryCodePrefixes',
    'libraryCodes',
    'erpStatusCodes',
    'brands',
    'bindingCodes',
    'priceGroupPockets',
    'contractFormats',
    'postingGroups',
    'mainGroups',
    'subgroups',
    'accountingMappings',
    'prioLevels',
    'contactRoles',
    'textTypes',
    'awards',
    'interestAges',
    'effortTypes',
    'productionLanguages',
    'countries',
    'agencyRightsSubCategory',
    'agencyRightsCategory',
    'retailerChannels',
    'customerSubGroups',
    'timePlanEntryTypes',
    'timePlanPackages',
    'governingCodes',
    'publishingHouses',
    'seasonYears',
    'seasonPeriods',
    'backlistSeasonYears',
    'backlistSeasonPeriods',
] as const satisfies readonly MetadataName[]

export const conceptMetadataNames = [
    'productionLanguages',
    'publishingHouses',
    'publishingLists',
    'contractTypes',
    'seasons',
    'seasonYears',
    'seasonPeriods',
    'bindingCodes',
    'contractFormats',
    'insidePapers',
    'layoutExpressions',
    'effortTypes',
    'imageTypes',
    'contactRoles',
    'interestAges',
    'targetGroups',
    'userAccessLevels',
    'productionTypes',
] as const satisfies readonly MetadataName[]

export type MetadataValue<K extends MetadataName> = Awaited<
    ReturnType<(typeof metadataRegistry)[K]>
>

export const metadataQueries = {
    key: (name: MetadataName) => ['metadata', name] as const,
    serviceBundle: () => serviceBundleQuery,
    byName: <K extends MetadataName>(name: K) =>
        queryOptions<MetadataValue<K>>({
            queryKey: ['metadata', name],
            queryFn: () => (metadataRegistry[name] as () => Promise<MetadataValue<K>>)(),
            staleTime: RequestStaleTime.metadata,
            gcTime: RequestGcTime.metadata,
        }),
}

const serviceBundleNames: ReadonlySet<string> = new Set(metadataServiceNames)

export const metadataInvalidationKeys = (name: MetadataName): QueryKey[] =>
    serviceBundleNames.has(name)
        ? [metadataQueries.key(name), serviceBundleQuery.queryKey]
        : [metadataQueries.key(name)]
